import { z } from 'zod';
import { FINANCING_DEFAULT_TERM_MONTHS } from './calculator-math';
import {
  type PersistedCalculator,
  type QuoteInput,
  quoteInputSchema,
} from '../types/calculator.types';

/**
 * calculator-persistence — pure localStorage read/write for the
 * calculator input (Slice 8 PR#1, polish PR installment). Extracted
 * from the hook so it can be unit-tested in the node test environment
 * (no jsdom) by injecting a storage stub.
 *
 * The persisted envelope is tenant-scoped and versioned. Reads from a
 * different tenant return null so the caller falls back to empty
 * defaults (reset-on-tenant-change).
 *
 * FORWARD-COMPAT (polish PR installment): payloads written before
 * `termMonths` was added are tolerant on read — the new field is
 * optional on the read shape and backfilled with
 * `FINANCING_DEFAULT_TERM_MONTHS` so the user keeps their quantity,
 * discount, etc. instead of being wiped to EMPTY on first open after
 * upgrade.
 */
export const CALCULATOR_STORAGE_KEY = 'globaleserp-calculator-v1';

/** Current persisted-envelope version. Bump on breaking schema changes. */
export const PERSISTED_VERSION = 2;

export type MinimalStorage = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): MinimalStorage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Permissive read schema — everything in `quoteInputSchema` is still
 * required, BUT `termMonths` is relaxed so legacy v1 payloads (written
 * before installment shipped) can be migrated by `readPersistedInput`
 * with a default value instead of being dropped to EMPTY.
 */
const legacyReadInputSchema = quoteInputSchema.extend({
  termMonths: z.number().int().positive().optional(),
});

/**
 * Wire envelope used by the writer. Holds `version: 2` so future
 * breaking schema changes can branch on the version.
 */
const writeEnvelopeSchema = z.object({
  version: z.literal(PERSISTED_VERSION),
  tenantId: z.string(),
  input: quoteInputSchema,
});

export function readPersistedInput(
  tenantId: string,
  storage: MinimalStorage | null = defaultStorage(),
): QuoteInput | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(CALCULATOR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = z
      .object({
        version: z.number(),
        tenantId: z.string(),
        input: legacyReadInputSchema,
      })
      .safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    if (parsed.data.tenantId !== tenantId) return null;
    const { termMonths = FINANCING_DEFAULT_TERM_MONTHS, ...rest } = parsed.data.input;
    return { ...rest, termMonths };
  } catch {
    return null;
  }
}

export function writePersistedInput(
  tenantId: string,
  input: QuoteInput,
  storage: MinimalStorage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    const payload: PersistedCalculator = {
      version: PERSISTED_VERSION,
      tenantId,
      input,
    };
    // Validate before writing so a bad input never reaches storage.
    writeEnvelopeSchema.parse(payload);
    storage.setItem(CALCULATOR_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private-mode / validation failures are non-fatal for a calculator.
  }
}
